import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core';
import type {
    ProofReadingOrchestrationService,
    ChaptersLoadedEvent,
    ChapterCheckStartedEvent,
    ChapterIssuesFoundEvent,
    ProofReadingCompletedEvent,
    ExecutionStatusChangedEvent,
    IssuesLoadingStatusChangedEvent,
    CurrentExecutionState,
} from '../proof-reading-orchestration-service';
import { ReportParagraphsService } from '../report-paragraphs-service';
import { CheckContentService } from '../check-content-service';
import type { SpecChapter } from '../../types';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import type { CheckData } from '../check-content-service';

@injectable()
export class ProofReadingOrchestrationServiceImpl implements ProofReadingOrchestrationService {
    private _onChaptersLoadEmitter = new Emitter<void>();
    private _onDidChaptersLoadEmitter = new Emitter<ChaptersLoadedEvent>();
    private _onChapterCheckStartedEmitter = new Emitter<ChapterCheckStartedEvent>();
    private _onChapterIssuesFoundEmitter = new Emitter<ChapterIssuesFoundEvent>();
    private _onProofReadingCompletedEmitter = new Emitter<ProofReadingCompletedEvent>();
    private _onProofReadingErrorEmitter = new Emitter<Error>();
    private _onExecutionStatusChangedEmitter = new Emitter<ExecutionStatusChangedEvent>();
    private _onIssuesLoadingStatusChangedEmitter = new Emitter<IssuesLoadingStatusChangedEvent>();

    private _currentExecutionState: CurrentExecutionState = {
        isExecuting: false,
    };

    constructor(
        @inject(ReportParagraphsService)
        private readonly _reportParagraphsService: ReportParagraphsService,
        @inject(CheckContentService)
        private readonly _checkContentService: CheckContentService,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
    ) {}

    get onChaptersLoad(): Event<void> {
        return this._onChaptersLoadEmitter.event;
    }

    get onDidChaptersLoad(): Event<ChaptersLoadedEvent> {
        return this._onDidChaptersLoadEmitter.event;
    }

    get onChapterCheckStarted(): Event<ChapterCheckStartedEvent> {
        return this._onChapterCheckStartedEmitter.event;
    }

    get onChapterIssuesFound(): Event<ChapterIssuesFoundEvent> {
        return this._onChapterIssuesFoundEmitter.event;
    }

    get onProofReadingCompleted(): Event<ProofReadingCompletedEvent> {
        return this._onProofReadingCompletedEmitter.event;
    }

    get onProofReadingError(): Event<Error> {
        return this._onProofReadingErrorEmitter.event;
    }

    get onExecutionStatusChanged(): Event<ExecutionStatusChangedEvent> {
        return this._onExecutionStatusChangedEmitter.event;
    }

    get onIssuesLoadingStatusChanged(): Event<IssuesLoadingStatusChangedEvent> {
        return this._onIssuesLoadingStatusChangedEmitter.event;
    }

    async loadChapters(): Promise<SpecChapter[]> {
        // 发射章节加载开始事件
        this._onChaptersLoadEmitter.fire();

        // 获取章节数据
        const chapters = await this._reportParagraphsService.getChaptersAndParagraphs();

        // 发射章节加载完成事件
        this._onDidChaptersLoadEmitter.fire({ chapters });

        return chapters;
    }

    private _abortController: AbortController | null = null;

    // eslint-disable-next-line max-lines-per-function, complexity
    async findIssues(options: {
        chapters: SpecChapter[];
        regenerate?: boolean;
    }): Promise<void> {
        const { chapters, regenerate } = options;

        // 创建新的 AbortController 用于中断
        this._abortController = new AbortController();
        const abortSignal = this._abortController.signal;

        // 设置执行状态和加载状态
        this._setExecutionStatus(true);
        this._setIssuesLoadingStatus(true);

        // 跟踪是否已经设置过加载状态为false
        let hasSetLoadingToFalse = false;

        try {
            const taskId = this._currentTaskService.taskId;

            // 为每个章节检查内容
            for (let i = 0; i < chapters.length; i++) {
                if (abortSignal.aborted) {
                    // 中断操作不视为错误，直接返回
                    return;
                }

                const chapter = chapters[i];

                // 发射章节检查开始事件
                this._onChapterCheckStartedEmitter.fire({
                    chapterType: chapter.title,
                    chapterIndex: i,
                    totalChapters: chapters.length,
                });

                // 调用CheckContentService
                const checkData: CheckData = {
                    taskId,
                    chapterType: chapter.title,
                    regenerate,
                };

                const issueGroups = await this._checkContentService.getParagraphIssueGroup(
                    checkData,
                    abortSignal,
                );

                // 第一次接收到问题组且存在非空issues时，设置加载状态为false
                if (!hasSetLoadingToFalse && issueGroups.length > 0 && issueGroups.some(group => group.issues.length > 0)) {
                    this._setIssuesLoadingStatus(false);
                    hasSetLoadingToFalse = true;
                }

                // 发射问题发现事件
                this._onChapterIssuesFoundEmitter.fire({
                    chapterType: chapter.title,
                    issueGroups: issueGroups,
                    chapterIndex: i,
                    totalChapters: chapters.length,
                });
            }

            // 发射完成事件
            this._onProofReadingCompletedEmitter.fire({});
        } catch (error) {
            // 发射错误事件
            this._onProofReadingErrorEmitter.fire(error as Error);
            // 不重新抛出错误，让调用方通过事件处理
        } finally {
            // 重置执行状态和加载状态
            this._setExecutionStatus(false);
            if (!hasSetLoadingToFalse) {
                this._setIssuesLoadingStatus(false);
            }
            // 清空 AbortController
            this._abortController = null;
        }
    }

    abortFindIssues(): void {
        if (this._abortController) {
            this._abortController.abort();
        }
    }

    private _setExecutionStatus(isExecuting: boolean): void {
        this._currentExecutionState.isExecuting = isExecuting;
        this._onExecutionStatusChangedEmitter.fire({ isExecuting });
    }

    private _setIssuesLoadingStatus(isLoadingIssues: boolean): void {
        this._onIssuesLoadingStatusChangedEmitter.fire({ isLoadingIssues });
    }

    getCurrentExecutionState(): CurrentExecutionState {
        return { ...this._currentExecutionState };
    }
}


